// Public Transactions tab — same layout as the internal version but
// without receipt-image previews. Producer names come back masked
// from the server ("Producer #<id>"); this component just renders.

import { useMemo, useState } from "react"
import { Receipt, Search } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type { OTTraceability } from "@/features/transit/api/types"

interface Props {
  traceability: OTTraceability
}

export function TraceabilityTransactionsTabPublic({ traceability }: Props) {
  const { t } = useTranslation("transit")
  const [search, setSearch] = useState("")

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return traceability.transactions
    return traceability.transactions.filter((tx) => {
      return (
        tx.receiptNumber.toLowerCase().includes(term) ||
        tx.producerName.toLowerCase().includes(term)
      )
    })
  }, [traceability.transactions, search])

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Receipt className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          {t("trace.transactions.title", {
            count: traceability.transactions.length,
          })}
        </h2>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("trace.transactions.searchPlaceholder")}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          {search
            ? t("trace.transactions.searchEmpty")
            : t("trace.transactions.empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.receipt")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.date")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.transactions.col.producer")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.bags")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.kg")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.transactions.col.amount")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((tx) => (
                <tr key={tx.id} className="text-foreground">
                  <td className="px-3 py-2 font-medium">
                    {tx.receiptNumber || `#${tx.id}`}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {tx.date ?? "—"}
                  </td>
                  <td className="px-3 py-2">{tx.producerName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {tx.numberOfBags}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatNumber(tx.netKilograms)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatNumber(tx.totalPrice)} CFA
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function formatNumber(value: number, fractionDigits = 0): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}
